import { useEffect, useState } from 'react';
import {
  GET_CURRENCY_CALCULATIONS,
  GET_TRADE_RATE_CURRENCIES,
  GET_USER_CURRENCIES
} from '../constants/aryGraphqlSchemas';
import {
  convertCalculationRateToSourceAndDestinationAmounts,
  getSourceToDestRate
} from '../utils/Utils';
import { useAryQuery } from './useAryGraphql';

const useSystemRate = ({ baseCurrencyCode, destinationCurrencyCode }) => {
  const [systemRate, setSystemRate] = useState(null);
  const [sourceAmount, setSourceAmount] = useState(null);

  const { data: userCurrencies } = useAryQuery({
    gqlConfig: GET_USER_CURRENCIES,
    items: `
      code
      decimalPlaces
      dollarRate
      isDivision
    `
  });

  const { data: currencyCalculationsData } = useAryQuery({
    gqlConfig: GET_CURRENCY_CALCULATIONS,
    variables: {
      pagination: {
        pageIndex: 0,
        pageCount: 60
      }
    },
    items: `
        currencyCalculations {
          sourceCurrencyCode
          destinationCurrencyCode
          calculationRate
        }
      `
  });

  const { data: fetchedSystemRate } = useAryQuery({
    gqlConfig: GET_TRADE_RATE_CURRENCIES,
    variables: {
      source: baseCurrencyCode,
      destination: destinationCurrencyCode
    },
    skip:
      !baseCurrencyCode ||
      !destinationCurrencyCode ||
      destinationCurrencyCode === baseCurrencyCode
  });

  const calculateSystemManagementRate = (currencies, calculationRate) => {
    const baseCurrency = currencies.find((c) => c.code === baseCurrencyCode);
    const destCurrency = currencies.find(
      (c) => c.code === destinationCurrencyCode
    );

    if (!baseCurrency || !destCurrency) return null;

    const sourceToDestRate = getSourceToDestRate(
      baseCurrency.dollarRate,
      baseCurrency.isDivision,
      destCurrency.dollarRate,
      destCurrency.isDivision
    );

    const adjustedRate =
      sourceToDestRate > 1 ? 1 / sourceToDestRate : sourceToDestRate;

    const {
      sourceAmount: baseAmount,
      destinationAmount: systemManagementRate
    } = convertCalculationRateToSourceAndDestinationAmounts(
      calculationRate,
      adjustedRate
    );
    setSourceAmount(baseAmount);

    return systemManagementRate;
  };

  const findMatchingRate = () =>
    currencyCalculationsData.currencyCalculations.find(
      (rate) =>
        (rate.sourceCurrencyCode === baseCurrencyCode ||
          rate.sourceCurrencyCode === destinationCurrencyCode) &&
        (rate.destinationCurrencyCode === destinationCurrencyCode ||
          rate.destinationCurrencyCode === baseCurrencyCode)
    )?.calculationRate;

  useEffect(() => {
    if (
      !userCurrencies ||
      !currencyCalculationsData ||
      !baseCurrencyCode ||
      !destinationCurrencyCode
    ) {
      return;
    }

    const filteredUserCurrencies = userCurrencies.filter(
      (currency) =>
        currency.code === baseCurrencyCode ||
        currency.code === destinationCurrencyCode
    );

    const matchingRate = findMatchingRate();

    if (matchingRate !== undefined && matchingRate !== null) {
      const calculatedRate = calculateSystemManagementRate(
        filteredUserCurrencies,
        matchingRate
      );
      setSystemRate(calculatedRate);
    } else if (fetchedSystemRate) {
      const fetchedRate =
        fetchedSystemRate < 1 ? 1 / fetchedSystemRate : fetchedSystemRate;
      setSourceAmount(null);
      setSystemRate(fetchedRate);
    } else if (baseCurrencyCode === destinationCurrencyCode) {
      setSystemRate(1);
    }
  }, [fetchedSystemRate, baseCurrencyCode, destinationCurrencyCode, currencyCalculationsData,
userCurrencies]);

  return { systemRate, sourceAmount, fetchedSystemRate };
};

export default useSystemRate;
